import React, { useState } from 'react';

const RoleCard = ({ role }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover:shadow-md transition-all duration-200 hover:border-primary-color">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2">
            {role.role}
          </h3>
          <div className="flex items-center text-sm text-gray-600 mb-1">
            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="font-medium">{role.client}</span>
          </div>
          <div className="flex items-center text-sm text-gray-600">
            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span>{role.department}</span>
          </div>
        </div>
        
        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
          role.status === 'Open' ? 'bg-green-100 text-green-800' :
          role.status === 'On Hold' ? 'bg-yellow-100 text-yellow-800' :
          role.status === 'Filled' ? 'bg-gray-100 text-gray-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {role.status}
        </span>
      </div>
      
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center text-xs text-gray-500">
          <div className="w-2 h-2 bg-primary-color rounded-full mr-2"></div>
          ID: {role.id}
        </div>
        <button className="text-primary-color hover:text-primary-color-dark text-sm font-medium transition-colors">
          View Details →
        </button>
      </div>
    </div>
  );
};

const OpenRolesCards = ({ roles }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [cardsPerPage, setCardsPerPage] = useState(12);

  // Calculate pagination
  const totalPages = Math.ceil(roles.length / cardsPerPage);
  const startIndex = (currentPage - 1) * cardsPerPage;
  const paginatedRoles = roles.slice(startIndex, startIndex + cardsPerPage);
  const startRecord = startIndex + 1;
  const endRecord = Math.min(startIndex + cardsPerPage, roles.length);

  // Handle page changes
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  if (roles.length === 0) {
    return (
      <div className="dashboard-card p-12 text-center">
        <svg className="w-20 h-20 mx-auto mb-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
        <h3 className="text-xl font-medium text-gray-800 mb-3">No roles match your filters</h3>
        <p className="text-gray-500 max-w-md mx-auto">
          Try adjusting your search criteria or clear the filters to see all available roles.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick Filter Chips */}
      <div className="dashboard-card p-4">
        <div className="flex flex-wrap gap-2">
          {/* Quick filters based on common attributes */}
          {[...new Set(roles.map(r => r.department))].slice(0, 6).map(dept => (
            <span key={dept} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {dept}
              <span className="ml-2 bg-blue-200 text-blue-800 rounded-full px-1 text-xs">
                {roles.filter(r => r.department === dept).length}
              </span>
            </span>
          ))}
        </div>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {paginatedRoles.map((role) => (
          <RoleCard key={role.id} role={role} />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="dashboard-card p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Results info */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-700">
                Showing {startRecord} to {endRecord} of {roles.length} roles
              </span>
              
              {/* Cards per page selector */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Show:</label>
                <select
                  value={cardsPerPage}
                  onChange={(e) => {
                    setCardsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-color"
                >
                  <option value={6}>6</option>
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={48}>48</option>
                </select>
              </div>
            </div>

            {/* Pagination controls */}
            <div className="flex items-center space-x-1">
              {/* Previous button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {/* Page numbers */}
              {getPageNumbers().map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 text-sm font-medium border ${
                    currentPage === pageNum
                      ? 'bg-primary-color text-white border-primary-color'
                      : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              ))}

              {/* Next button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary stats */}
      <div className="dashboard-card p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Role Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-color">{roles.length}</div>
            <div className="text-sm text-gray-600">Total Roles</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {roles.filter(role => role.status === 'Open').length}
            </div>
            <div className="text-sm text-gray-600">Open Positions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {[...new Set(roles.map(r => r.client))].length}
            </div>
            <div className="text-sm text-gray-600">Clients</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {[...new Set(roles.map(r => r.department))].length}
            </div>
            <div className="text-sm text-gray-600">Departments</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenRolesCards;